// components/steps/Step4ReviewSubmit.tsx - Redesigned with Concise View Style
import React from 'react';
import { FormData, Gate, Personnel, ApproverSelection, ReviewerSelection, Region } from '../../types/GatePassTypes';
import { getRegionName, formatDate, isVisaExpired } from '../../utils/GatePassUtils';
import './Step4.css';

interface Step4ReviewSubmitProps {
  formData: FormData;
  selectedGates: Gate[];
  selectedPersonnel: Personnel[];
  approverSelection: ApproverSelection | null;
  reviewerSelection: ReviewerSelection | null;
  regions: Region[];
  onValidationChange?: (isValid: boolean) => void;
}

export const Step4ReviewSubmit: React.FC<Step4ReviewSubmitProps> = ({
  formData,
  selectedGates,
  selectedPersonnel,
  approverSelection,
  reviewerSelection,
  regions,
  onValidationChange
}) => {
  // Validation logic - Step is valid when all required data is present
  const isStepValid: boolean = Boolean(
    formData.name &&
    formData.email &&
    formData.regionId &&
    selectedGates.length > 0 &&
    selectedPersonnel.length > 0 &&
    approverSelection &&
    approverSelection.selectedApprovers.length > 0
  );

  // Notify parent component of validation status changes
  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isStepValid);
    }
  }, [isStepValid, onValidationChange]);

  // Count expired visas
  const expiredVisaCount = selectedPersonnel.filter(p => isVisaExpired(p.visaExpiry)).length;

  // Helper functions
  const formatPassType = (passType: string): string => {
    switch (passType) {
      case 'single_visit': return 'Single Visit';
      case 'short_term': return 'Short Term';
      case 'long_term': return 'Long Term';
      default: return passType;
    }
  };

  const formatRequestType = (requestType: string): string => {
    switch (requestType) {
      case 'new': return 'New';
      case 'supplementary': return 'Supplementary';
      default: return requestType;
    }
  };

  const calculateDuration = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) return '—';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  };

  return (
    <div className="step-content">

      {/* Expired visa warning */}
      {expiredVisaCount > 0 && (
        <div className="form-warning" style={{ marginBottom: '16px' }}>
          <span className="warning-icon">⚠️</span>
          <span className="warning-text">
            <strong>{expiredVisaCount} personnel</strong> have expired visas. Gate security may reject entry.
          </span>
        </div>
      )}

      <div className="review-container">

        {/* ── Basic Information ─────────────────────────────────── */}
        <div className="selection-section">
          <div className="s2-section-header">
            <span className="s2-section-title">Basic Information</span>
          </div>

          <div className="review-fields-grid">
            <div className="review-field">
              <span className="review-field-label">Name</span>
              <span className="review-field-value">{formData.name}</span>
            </div>
            <div className="review-field">
              <span className="review-field-label">Email</span>
              <span className="review-field-value">{formData.email}</span>
            </div>
            <div className="review-field">
              <span className="review-field-label">Company</span>
              <span className="review-field-value">{formData.companyName}</span>
            </div>
            <div className="review-field">
              <span className="review-field-label">Region</span>
              <span className="review-field-value">{getRegionName(formData.regionId, regions)}</span>
            </div>
            <div className="review-field">
              <span className="review-field-label">Request Type</span>
              <span className="review-field-value">{formatRequestType(formData.requestType || 'new')}</span>
            </div>
            <div className="review-field">
              <span className="review-field-label">Pass Type</span>
              <span className="review-field-value">{formatPassType(formData.passType || 'single_visit')}</span>
            </div>
            {formData.startDate && (
              <div className="review-field">
                <span className="review-field-label">Start Date</span>
                <span className="review-field-value">{formatDate(formData.startDate)}</span>
              </div>
            )}
            {formData.endDate && (
              <div className="review-field">
                <span className="review-field-label">End Date</span>
                <span className="review-field-value">{formatDate(formData.endDate)}</span>
              </div>
            )}
            {formData.startDate && formData.endDate && (
              <div className="review-field">
                <span className="review-field-label">Duration</span>
                <span className="review-field-value">{calculateDuration(formData.startDate, formData.endDate)}</span>
              </div>
            )}
            {formData.contractNumber && (
              <div className="review-field">
                <span className="review-field-label">Contract Number</span>
                <span className="review-field-value">{formData.contractNumber}</span>
              </div>
            )}
            {formData.passIdentificationName && (
              <div className="review-field review-field--full">
                <span className="review-field-label">Pass ID</span>
                <span className="review-field-value">{formData.passIdentificationName}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Selected Gates ────────────────────────────────────── */}
        {selectedGates.length > 0 && (
          <div className="selection-section">
            <div className="s2-section-header">
              <span className="s2-section-title">Selected Gates</span>
              <span className="s2-count-badge">{selectedGates.length}</span>
            </div>
            <div className="selected-items-table">
              <table className="selection-table">
                <thead>
                  <tr>
                    <th>Gate</th>
                    <th>Location</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedGates.map((gate) => (
                    <tr key={gate.gateId}>
                      <td className="gate-number"><strong> {gate.gateNumber}</strong></td>
                      <td className="gate-location">{gate.locationName}</td>
                      <td>
                        <span className={`status-text ${gate.isActive ? 'active' : 'inactive'}`}>
                          {gate.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Selected Personnel ────────────────────────────────── */}
        {selectedPersonnel.length > 0 && (
          <div className="selection-section">
            <div className="s2-section-header">
              <span className="s2-section-title">Selected Personnel</span>
              <span className="s2-count-badge">{selectedPersonnel.length}</span>
              {expiredVisaCount > 0 && (
                <span className="s2-warn-badge">{expiredVisaCount} expired visa{expiredVisaCount > 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="selected-items-table">
              <table className="selection-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>National ID</th>
                    <th>Nationality</th>
                    <th>Passport Expiry</th>
                    <th>Visa Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPersonnel.map((person) => {
                    const expired = isVisaExpired(person.visaExpiry);
                    const personAny = person as any;
                    const passportExpiry = personAny.passportExpiryDate || personAny.passportExpiry || personAny.passport_expiry_date;
                    const isPassportExpired = passportExpiry ? new Date(passportExpiry) < new Date() : false;
                    const displayName = `${personAny.firstName || ''} ${personAny.lastName || ''}`.trim()
                      || personAny.fullName
                      || personAny.name
                      || '—';
                    return (
                      <tr key={person.personnelId} className={expired ? 'visa-issue-row' : ''}>
                        <td className="personnel-name">
                          <strong>{displayName}</strong>
                        </td>
                        <td className="personnel-id">{person.nationalIdNo || '—'}</td>
                        <td className="personnel-nationality">{person.nationality || '—'}</td>
                        <td className="personnel-date">
                          {passportExpiry ? (
                            <span className={isPassportExpired ? 'date-expired' : 'date-valid'}>
                              {new Date(passportExpiry).toLocaleDateString()}
                              {isPassportExpired && <span className="expiry-label">Expired</span>}
                            </span>
                          ) : <span className="date-missing">—</span>}
                        </td>
                        <td className="personnel-date">
                          {person.visaExpiry ? (
                            <span className={expired ? 'date-expired' : 'date-valid'}>
                              {formatDate(person.visaExpiry)}
                              {expired && <span className="expiry-label">Expired</span>}
                            </span>
                          ) : <span className="date-missing">No Visa</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Approvers ─────────────────────────────────────────── */}
        {approverSelection && approverSelection.selectedApprovers.length > 0 && (
          <div className="selection-section">
            <div className="s2-section-header">
              <span className="s2-section-title">Approvers</span>
              <span className="s2-count-badge">{approverSelection.selectedApprovers.length}</span>
            </div>
            <div className="selected-items-table">
              <table className="selection-table">
                <thead>
                  <tr>
                    <th>Division</th>
                    <th>Approver Name</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {approverSelection.selectedApprovers.map((approver) => (
                    <tr key={approver.userId}>
                      <td className="approver-function"><strong>{approverSelection.function?.functionName || '—'}</strong></td>
                      <td className="approver-name"><strong>{approver.userName}</strong></td>
                      <td className="approver-email">{approver.userEmail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Reviewers ─────────────────────────────────────────── */}
        {reviewerSelection && reviewerSelection.selectedReviewers.length > 0 && (
          <div className="selection-section">
            <div className="s2-section-header">
              <span className="s2-section-title">Reviewers</span>
              <span className="s2-count-badge">{reviewerSelection.selectedReviewers.length}</span>
            </div>
            <div className="selected-items-table">
              <table className="selection-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Division</th>
                    <th>Reviewer Name</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewerSelection.selectedReviewers.map((reviewer) => (
                    <tr key={reviewer.userId}>
                      <td className="reviewer-company"><strong>{reviewerSelection.company?.companyName || '—'}</strong></td>
                      <td className="reviewer-function"><strong>{reviewerSelection.function?.functionName || '—'}</strong></td>
                      <td className="reviewer-name"><strong>{reviewer.userName}</strong></td>
                      <td className="reviewer-email">{reviewer.userEmail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );

};